import { Drawer, Button, ButtonProps, Tooltip, message } from 'antd';
import { useCallback, useContext } from 'react';
import { useTranslate } from '@tolgee/react';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import Icon, { LeftOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { NewTodoForm } from './NewTodoForm';
import { ReactComponent as TodoNewSvg } from '../assets/todo-new.svg';
import { TypeSourceContext } from './TypeSourceContext';
import { getTaskByStoreId } from './api';
import { zIndex } from 'services/config/styles';
import { useUnmount } from 'ahooks';
import { useClearTodoCache } from './TodoCache';

const newTodoDrawerVisibleState = atom<{
  visible: boolean;
  messageId?: string;
}>({ visible: false, messageId: undefined });

export const useNewTodoDrawerVisible = () => useAtomValue(newTodoDrawerVisibleState);
export const useSetNewTodoDrawerVisible = () => useSetAtom(newTodoDrawerVisibleState);

export type TypeSource = {
  type: Todo.Type;
  typeLabel: string;
  results: string[];
  subTypes: string[];
  origin: Todo.Policy;
  subTypeTrees: Todo.SubTypeTreeNode[];
  enableManualCreate: boolean;
  enable: boolean;
}[];

export const NewTodo = () => {
  const { visible } = useNewTodoDrawerVisible();
  const setVisible = useSetNewTodoDrawerVisible();

  useUnmount(() => {
    setVisible({ visible: false });
  });

  const { t } = useTranslate();
  const { taskId } = useContext(TypeSourceContext);
  const clearTodoCache = useClearTodoCache();

  if (!taskId) {
    return null;
  }

  return (
    <NewTodoForm visible={visible}>
      {(node, { isSubmitting, submitForm, resetForm }) => {
        const close = () => {
          setVisible({ visible: false });
          clearTodoCache();
          resetForm();
        };
        return (
          <Drawer
            placement="right"
            closable={false}
            visible={visible}
            getContainer={false}
            destroyOnClose
            width={420}
            style={{
              position: 'absolute',
              zIndex: zIndex.low,
            }}
            bodyStyle={{
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
            footer={
              <div className="flex justify-end items-center">
                <Button onClick={close}>{t('translation.取消')}</Button>
                <Button
                  loading={isSubmitting}
                  onClick={submitForm}
                  className="ml-[16px]"
                  type="primary"
                >
                  {t('ViewStartups.reset.form.submit.btn')}
                </Button>
              </div>
            }
          >
            {/*header*/}
            <div className="flex-shrink-0 h-[40px] border-0 border-b border-solid border-[#f0f2f5] px-[8px] flex items-center relative top-0 left-0 right-0">
              <Button
                onClick={close}
                className="!px-0 text-[#515359] absolute left-0"
                type="text"
                icon={<LeftOutlined />}
              >
                {t('phrase.返回')}
              </Button>
              <div
                className={css`
                  position: absolute;
                  left: 50%;
                  top: 50%;
                  transform: translate(-50%, -50%);
                  color: #515359;
                  font-size: 14px;
                `}
              >
                {t('todo_new')}
              </div>
            </div>

            <div className="flex-grow basis-auto overflow-auto">{node}</div>
          </Drawer>
        );
      }}
    </NewTodoForm>
  );
};

export const useOpenNewTodoDrawer = () => {
  const setVisible = useSetNewTodoDrawerVisible();
  return useCallback((messageId?: string) => {
    setVisible({
      visible: true,
      messageId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export const NewTodoButton = (props: ButtonProps) => {
  const open = useOpenNewTodoDrawer();
  const { t } = useTranslate();
  const { taskId } = useContext(TypeSourceContext);
  return (
    <Button
      onClick={() => {
        if (taskId) {
          open();
        } else {
          message.warning(t('no_task_tip'));
        }
      }}
      {...props}
    >
      +{t('todo_new')}
    </Button>
  );
};

export const NewTodoIcon = (props: { messageId: string; storeId: string }) => {
  const open = useOpenNewTodoDrawer();
  const { t } = useTranslate();
  const { storeId, messageId } = props;

  return (
    <Tooltip title={t('todo_new')}>
      <span
        className="hoverVisible px-[4px] pt-[2px]"
        onClick={() => {
          getTaskByStoreId(storeId).then((res) => {
            if (res?.task?.id) {
              open(messageId);
            } else {
              message.warning(t('no_task_tip'));
            }
          });
        }}
      >
        <Icon component={TodoNewSvg} style={{ color: '#515359', fontSize: 16 }} />
      </span>
    </Tooltip>
  );
};
